// Textes de la fiche acteur (voix « tu » de l'app), calculés depuis la fiche au format simple.
// Repris du prototype v3 validé le 02/10/2026 ; la fiche elle-même suit la maquette v6
// « icônes dessinées ». Un seul module pour l'aperçu du formulaire partenaires et pour
// l'écran de l'app : la fiche n'existe qu'en une version.

import type { IconName } from './icons';
import {
  ACTIVITIES,
  CONDITION_CODES,
  CONDITION_NAMES,
  DELAY_TEXT,
  DOMAIN_BY_CODE,
  RANGE_CODES,
  RANGE_NAMES,
  subtypeName,
} from './referentials';
import type { NumberInput, PartnerSheet, RuleSense, SheetLinks } from './types';

export type SheetKind = 'online' | 'local';
export type Tone = 'green' | 'coral' | 'sunny';
export type TagTone = 'online' | 'local' | 'money' | 'service';

export type DisplayLine = { key: keyof SheetLinks; label: string; value: string; icon: IconName; tone: Tone; href?: string };
export type DisplayRule = { sense: RuleSense; text: string };

export function sheetKind(sheet: PartnerSheet): SheetKind {
  return sheet.identite.canal === 'magasin' ? 'local' : 'online';
}

export function activityProfile(sheet: PartnerSheet) {
  const activities = sheet.identite.activites;
  const selling = activities.includes('vente_pour_compte') || activities.includes('depot_vente');
  return {
    rachat: activities.includes('rachat'),
    vente: selling,
    plateforme: activities.includes('plateforme'),
    paye: selling || activities.includes('rachat'),
    vend: selling || activities.includes('rachat') || activities.includes('plateforme'),
  };
}

export function numberText(value: NumberInput): string {
  if (value === null || value === undefined) return '';
  return String(value).trim().replace('.', ',');
}

// Un montant en euros venu du serveur garde ses centimes : 1.2 → « 1,20 ».
export function euroText(value: NumberInput): string {
  if (typeof value === 'number' && !Number.isInteger(value)) return value.toFixed(2).replace('.', ',');
  return numberText(value);
}

export function numberValue(value: NumberInput): number | null {
  const text = numberText(value).replace(/\s/g, '').replace(',', '.');
  if (!text) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

function percent(value: NumberInput): string {
  return `${numberText(value)} %`;
}

function capitalize(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

export function joinOr(list: string[]): string {
  return list.length <= 1 ? list.join('') : `${list.slice(0, -1).join(', ')} ou ${list[list.length - 1]}`;
}

// « de comme neuf à moyen », « tous », « comme neuf, moyen ».
export function rangeText(indexes: number[], names: string[]): string {
  const on = [...indexes].sort((a, b) => a - b);
  if (!on.length) return '';
  if (on.length === names.length) return 'tous';
  if (on.length === 1) return names[on[0]].toLowerCase();
  if (on[on.length - 1] - on[0] === on.length - 1) {
    return `de ${names[on[0]].toLowerCase()} à ${names[on[on.length - 1]].toLowerCase()}`;
  }
  return on.map((index) => names[index].toLowerCase()).join(', ');
}

export function conditionIndexes(sheet: PartnerSheet): number[] {
  return sheet.accepte.etats.map((code) => CONDITION_CODES.indexOf(code)).filter((index) => index >= 0);
}

export function conditionRangeText(sheet: PartnerSheet): string {
  return rangeText(conditionIndexes(sheet), CONDITION_NAMES);
}

export function rangesText(sheet: PartnerSheet): string {
  const indexes = sheet.accepte.gammes.map((code) => RANGE_CODES.indexOf(code)).filter((index) => index >= 0);
  return rangeText(indexes, RANGE_NAMES).replace(/^tous$/, 'toutes');
}

export function typeLine(sheet: PartnerSheet): string {
  const activities = sheet.identite.activites;
  const online = sheetKind(sheet) === 'online';
  if (activities.includes('vente_pour_compte')) return online ? 'Service en ligne qui vend pour toi' : 'Dépôt-vente';
  if (activities.includes('depot_vente')) return 'Dépôt-vente';
  if (activities.includes('rachat')) return online ? 'Rachète en ligne' : 'Magasin qui rachète sur place';
  if (activities.includes('plateforme')) return 'Vente entre particuliers';
  if (activities.includes('don')) return 'Récupère les dons';
  if (activities.includes('recyclage')) return 'Point de recyclage';
  if (activities.includes('reparation')) return 'Réparateur';
  return '';
}

export function payText(sheet: PartnerSheet): string {
  const pay = sheet.deroulement.remuneration;
  if (!pay) return '';
  if (pay.type === 'fixe') return 'Un prix fixé à l’avance';
  const tiers = pay.tranches.filter((tier) => numberText(tier.pourcent));
  if (!tiers.length) return '';
  let text;
  if (tiers.length === 1 && !numberText(tiers[0].a)) {
    text = `${percent(tiers[0].pourcent)} du prix de vente`;
  } else {
    text = tiers
      .map((tier, index) =>
        numberText(tier.a)
          ? `${percent(tier.pourcent)} jusqu’à ${numberText(tier.a)} €`
          : `${percent(tier.pourcent)}${index ? ' au-delà' : ''}`,
      )
      .join(', ');
    if (pay.application === 'prix_entier') text += ', sur le prix entier';
  }
  if (numberText(pay.frais_article)) text += `, moins ${euroText(pay.frais_article)} € par article mis en vente`;
  return text;
}

function moneyHow(sheet: PartnerSheet): string {
  const flow = sheet.deroulement;
  const means: Record<string, string> = { virement: 'virement', especes: 'espèces', cheque: 'chèque' };
  const parts = [];
  if (flow.paye_en.includes('argent')) {
    parts.push(flow.moyens.length ? `par ${joinOr(flow.moyens.map((mean) => means[mean]))}` : 'en argent');
  }
  if (flow.paye_en.includes('bon_achat')) {
    parts.push(`en bon d’achat${numberText(flow.bonus_bon_achat) ? ` (+${numberText(flow.bonus_bon_achat)} %)` : ''}`);
  }
  return parts.join(' ou ');
}

export function delayLabel(sheet: PartnerSheet): string {
  const arrival = sheet.deroulement.arrivee;
  if (arrival.includes('colis')) return 'Délai moyen entre l’envoi du colis et le paiement';
  if (arrival.includes('collecte')) return 'Délai moyen entre l’enlèvement et le paiement';
  return 'Délai moyen entre le dépôt et le paiement';
}

export function showsDelay(sheet: PartnerSheet): boolean {
  const profile = activityProfile(sheet);
  return profile.vente || (profile.rachat && sheet.deroulement.arrivee.some((arrival) => arrival !== 'magasin'));
}

// « Comment ça se passe » : une ligne par info, icône colorée selon le sens.
export function sheetLines(sheet: PartnerSheet, links: SheetLinks = {}): DisplayLine[] {
  const lines: DisplayLine[] = [];
  const flow = sheet.deroulement;
  const profile = activityProfile(sheet);
  const local = sheetKind(sheet) === 'local';
  const name = sheet.identite.nom || 'eux';
  const push = (line: Omit<DisplayLine, 'href'>) => lines.push({ ...line, href: links[line.key] });

  if (flow.arrivee.length && !profile.plateforme) {
    const parts = flow.arrivee.map((arrival) => {
      if (arrival === 'magasin') {
        // Un point de don, de recyclage ou un réparateur n'est pas un magasin (David, 03/10/2026).
        let text = 'Tu l’apportes sur place';
        if (local) text = profile.rachat || profile.vente ? 'Tu viens en magasin avec ton objet' : 'Tu apportes ton objet sur place';
        return `${text}${flow.sur_rdv ? ', sur rendez-vous' : ''}`;
      }
      if (arrival === 'colis') return `Tu l’envoies par colis${flow.envoi_gratuit ? ', sans frais' : ''}`;
      if (arrival === 'point_relais') return 'Tu le déposes en point relais';
      return `Ils viennent le chercher chez toi${flow.zone ? ` (${flow.zone})` : ''}`;
    });
    const icons: Record<string, IconName> = { colis: 'package', magasin: 'store', collecte: 'truck', point_relais: 'pin' };
    const icon = icons[flow.arrivee[0]] || 'package';
    // « Ils viennent le chercher chez toi ou tu l’envoies par colis » : pas de majuscule
    // au milieu de la phrase.
    const value = parts.map((part, index) => (index ? part.charAt(0).toLowerCase() + part.slice(1) : part)).join(' ou ');
    push({ key: 'remise', label: 'Remise', value, icon, tone: 'green' });
  }
  const activities = sheet.identite.activites;
  if (activities.includes('vente_pour_compte')) {
    push({ key: 'vente', label: 'Vente', value: `Tri, photos, prix et envoi aux acheteurs faits par ${name}`, icon: 'tag', tone: 'green' });
  } else if (activities.includes('depot_vente')) {
    push({ key: 'vente', label: 'Vente', value: `Mis en vente par ${name}`, icon: 'tag', tone: 'green' });
  }
  if (profile.rachat && flow.prix_donne.length) {
    const texts: Record<string, string> = {
      site: 'en ligne sur leur site, avant d’envoyer',
      liberio: 'sur photos, via Liberio',
      sur_place: 'objet testé et estimé sur place',
    };
    push({ key: 'estimation', label: 'Estimation', value: capitalize(joinOr(flow.prix_donne.map((price) => texts[price]))), icon: 'search', tone: 'green' });
  }
  if (profile.vente) {
    const text = payText(sheet);
    if (text) push({ key: 'part', label: 'Ta part', value: text, icon: 'coins', tone: 'coral' });
  }
  if (profile.plateforme && flow.frais_vendeur) {
    const value =
      flow.frais_vendeur.type === 'aucun'
        ? 'Aucun frais pour le vendeur'
        : `Commission de ${numberText(flow.frais_vendeur.commission) ? percent(flow.frais_vendeur.commission) : '…'} sur la vente`;
    push({ key: 'part', label: 'Frais', value, icon: 'coins', tone: 'coral' });
  }
  if (profile.paye && flow.paye_en.length) {
    let when;
    if (profile.vente) when = 'Après la vente';
    else if (flow.arrivee.includes('magasin') && local) when = 'Tout de suite, en magasin';
    else when = 'Après réception de l’objet';
    push({ key: 'paiement', label: 'Paiement', value: `${when}, ${moneyHow(sheet)}`, icon: 'wallet', tone: 'coral' });
  }
  if (showsDelay(sheet) && flow.delai) {
    push({ key: 'delai', label: 'Délai', value: `En général ${DELAY_TEXT[flow.delai]} avant d’être payé`, icon: 'hourglass', tone: 'sunny' });
  }
  return lines;
}

// Étiquettes du haut de fiche : déduites des réponses, jamais demandées (4 au plus).
export function sheetTags(sheet: PartnerSheet): Array<[string, TagTone]> {
  const flow = sheet.deroulement;
  const profile = activityProfile(sheet);
  const local = sheetKind(sheet) === 'local';
  const tags: Array<[string, TagTone]> = [local ? ['À proximité', 'local'] : ['En ligne', 'online']];
  if (sheet.identite.activites.includes('vente_pour_compte')) tags.push(['Vend pour toi', 'service']);
  if (profile.rachat && flow.arrivee.includes('magasin') && local && flow.paye_en.includes('argent')) {
    tags.push(['Paiement immédiat', 'money']);
  }
  if (profile.paye && flow.paye_en.length === 1 && flow.paye_en[0] === 'bon_achat') tags.push(['Bon d’achat', 'money']);
  if (flow.arrivee.includes('colis') && flow.envoi_gratuit) tags.push(['Envoi gratuit', 'money']);
  if (flow.arrivee.includes('collecte')) tags.push(['Collecte à domicile', 'service']);
  if (sheet.identite.enseigne_nationale) tags.push(['Enseigne nationale', 'service']);
  if (profile.rachat && flow.prix_donne.includes('liberio')) tags.push(['Estimation via Liberio', 'service']);
  return tags.slice(0, 4);
}

// Minimum par lot : on peut grouper plusieurs objets pour l'atteindre (David, 06/10/2026).
// `joiner` vaut « , » dans une précision, déjà introduite par « Catégorie : ».
function minValueText(montant: NumberInput, base: string, joiner = ' : '): string {
  return base === 'lot'
    ? `au moins ${numberText(montant)} € en tout${joiner}tu peux grouper plusieurs objets pour y arriver`
    : `revendable au moins ${numberText(montant)} € par objet`;
}

// Les précisions d'une catégorie, en une phrase chacune (formulaire et fiche).
export function precisionTexts(category: PartnerSheet['accepte']['categories'][number]) {
  const domain = DOMAIN_BY_CODE[category.domaine];
  const domainName = domain?.name || category.domaine;
  const typeName = (code: string) => domain?.types.find((type) => type[0] === code)?.[1] || code;
  const precisions = category.precisions || {};
  const texts: Array<{ key: string; form: string; sheet: string }> = [];
  for (const [code, subtypes] of Object.entries(precisions.sous_types || {})) {
    const names = subtypes.map(subtypeName);
    const list = joinOr(names.map((label, index) => (index ? label.toLowerCase() : label)));
    texts.push({
      key: `sous_types:${code}`,
      form: `${typeName(code)} : ${joinOr(names.map((label) => label.toLowerCase()))} seulement`,
      sheet: `${list} seulement`,
    });
  }
  if (precisions.etats?.length) {
    const names = precisions.etats.map((code) => CONDITION_NAMES[CONDITION_CODES.indexOf(code)].toLowerCase());
    texts.push({ key: 'etats', form: `États pour cette catégorie : ${joinOr(names)}`, sheet: `${domainName} : ${joinOr(names)}` });
  }
  if (precisions.valeur_min && numberValue(precisions.valeur_min.montant)) {
    texts.push({
      key: 'valeur_min',
      form: 'Minimum pour cette catégorie',
      sheet: `${domainName} : ${minValueText(precisions.valeur_min.montant, precisions.valeur_min.base, ', ')}`,
    });
  }
  if (precisions.activites?.length) {
    const names = precisions.activites.map((code) => (ACTIVITIES.find((item) => item[0] === code)?.[1] || code).toLowerCase());
    texts.push({
      key: 'activites',
      form: `Pour cette catégorie : ${joinOr(names)} seulement`,
      sheet: `${domainName} : ${joinOr(names)} seulement`,
    });
  }
  return texts;
}

// « Leurs conditions » : valeur minimum, conditions de l'acteur, précisions par catégorie,
// puis ce que devient un objet refusé ou invendu.
// « Leurs conditions ». Avec `objectDomain` (le domaine de l'objet scanné), on ne garde que
// ce qui le concerne : une condition limitée à d'autres domaines, ou les précisions d'une autre
// catégorie, ne s'affichent pas (David, 06/10/2026). Sans objet (aperçu du formulaire), tout.
export function sheetRules(
  sheet: PartnerSheet,
  options: { objectDomain?: string; objectType?: string; objectSubtype?: string } = {},
): DisplayRule[] {
  const domain = options.objectDomain || '';
  const objectType = options.objectType || '';
  const objectSubtype = options.objectSubtype || '';
  // Une condition visant d'autres objets que l'objet scanné ne s'affiche pas. Chaque niveau ne
  // restreint que le sien : une sous-catégorie citée ne restreint que son domaine, un sous-type
  // cité que sa sous-catégorie (Bureau Vallée : « seulement les cartables » ne cache rien aux
  // autres fournitures). Ce que le scan n'a pas trouvé ne cache rien.
  const concerns = (rule: PartnerSheet['accepte']['conditions'][number]) => {
    if (!domain) return true;
    if (rule.categories?.length && !rule.categories.includes(domain)) return false;
    const typesOfDomain = (rule.sous_categories || []).filter((code) =>
      DOMAIN_BY_CODE[domain]?.types.some(([typeCode]) => typeCode === code),
    );
    if (!objectType) return true;
    if (typesOfDomain.length && !typesOfDomain.includes(objectType)) return false;
    const subtypesOfType = rule.sous_types_par_sous_categorie?.[objectType] || [];
    return !objectSubtype || !subtypesOfType.length || subtypesOfType.includes(objectSubtype);
  };
  const rules: DisplayRule[] = [];
  const minValue = sheet.accepte.valeur_min;
  if (minValue && numberValue(minValue.montant)) {
    rules.push({ sense: 'ok', text: capitalize(minValueText(minValue.montant, minValue.base)) });
  }
  sheet.accepte.conditions
    .filter(concerns)
    .forEach((rule) => rules.push({ sense: rule.sens, text: rule.texte }));
  sheet.accepte.categories
    .filter((category) => category.sous_categories.length && (!domain || category.domaine === domain))
    .forEach((category) =>
      precisionTexts(category)
        // « Vélo électrique seulement » ne sert que pour un vélo, pas pour une trottinette.
        .filter((text) => !objectType || !text.key.startsWith('sous_types:') || text.key === `sous_types:${objectType}`)
        .forEach((text) => rules.push({ sense: 'ok', text: text.sheet })),
    );
  if (activityProfile(sheet).vente && sheet.deroulement.invendu.length) {
    const texts: Record<string, string> = {
      rendu_gratuit: 'rendu gratuitement',
      rendu_frais: 'rendu contre des frais',
      donne: 'donné ou recyclé',
    };
    rules.push({ sense: 'info', text: `Objet refusé ou invendu : ${sheet.deroulement.invendu.map((item) => texts[item]).join(', sinon ')}` });
  }
  return rules.filter((rule) => rule.text);
}

export function acceptedDomains(sheet: PartnerSheet) {
  return sheet.accepte.categories
    .filter((category) => category.sous_categories.length && DOMAIN_BY_CODE[category.domaine])
    .map((category) => DOMAIN_BY_CODE[category.domaine]);
}

export function prettyUrl(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.length > 1 && parsed.pathname.length < 26 ? parsed.pathname : '';
    return `${parsed.hostname.replace(/^www\./, '')}${path}`.replace(/\/$/, '');
  } catch {
    return url;
  }
}

export function initials(name: string): string {
  return (
    (name || '?')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0].toUpperCase())
      .join('') || '?'
  );
}

export function frenchDate(isoDate: string | null | undefined, withYear: boolean): string {
  if (!isoDate) return '';
  const parsed = new Date(`${isoDate.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('fr-FR', withYear ? { day: 'numeric', month: 'long', year: 'numeric' } : { day: 'numeric', month: 'long' });
}

export function callToAction(sheet: PartnerSheet): string {
  const activities = sheet.identite.activites;
  const name = sheet.identite.nom;
  if (activities.length === 1 && activities[0] === 'don') return `Donner à ${name} →`;
  if (activities.length === 1 && activities[0] === 'recyclage') return `Recycler chez ${name} →`;
  return sheetKind(sheet) === 'local' ? `Vendre chez ${name} →` : `Vendre sur ${name} →`;
}
